import { useEffect, useState } from "react";
import ImageWrapper from "../ImageComponent/ImageWrapper";
import AddressFlowModal from '@/components/AddressFields/AddressFlowModal';
import ConfirmDeleteModal from '@/components/AddressFields/ConfirmDeleteModal';
import AddressCard from '@/components/AddressFields/AddressCard';

const AddressListing = ({
  defaultAddress,
  addresses = [],
  fetchAddresses,
  handleAddAddress,
  handleDeleteAddress,
  userData,
  otpHandlers,
  closeModal,
  onSelectAddress
}) => {
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editAddressData, setEditAddressData] = useState(null);
  const [deleteAddress, setDeleteAddress] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);

  /* 🔥 AUTO OPEN ADD ADDRESS IF EMPTY */
  useEffect(() => {
    if (!addresses || addresses.length === 0) {
      setShowAddressModal(true);
    }
  }, [addresses]);
  /* ✅ AUTO SELECT DEFAULT ADDRESS (GOOD UX) */
  useEffect(() => {
    if (!selectedAddress && addresses?.length > 0) {
      const defaultAddressObj = addresses.find((a) => a?._id === defaultAddress?._id);
      if (defaultAddressObj) {
        setSelectedAddress(defaultAddressObj);
      }
    } else if (selectedAddress) {
      // Clear selectedAddress if it is no longer in the list (e.g., deleted)
      const stillExists = addresses?.find((a) => a?._id === selectedAddress?._id);
      if (!stillExists) {
        setSelectedAddress(null);
      }
    }
  }, [addresses, defaultAddress?._id, selectedAddress]);

  const closeFlowModal = () => {
    setEditAddressData(null);
    setShowAddressModal(false);
  };

  return (
    <>
      {/* ================= LISTING MODAL ================= */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ModalTransparentColor">
        <div className="bg-white w-full h-full md:w-3/6 lg:w-2/5 xl:w-1/3 md:h-[85vh] md:rounded-lg flex flex-col">

          {/* HEADER */}
          <header className="flex justify-between items-center border-b p-4">
            <h2 className="text-lg font-semibold">Select Address</h2>
            <button className="w-4 h-4" onClick={closeModal}>
              <ImageWrapper
                src="/icons/Cancelbutton.svg"
                alt="Close"
              />
            </button>
          </header>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-4 pb-40 md:pb-4">
            {addresses.length > 0 ? (
              <div className="flex flex-col gap-3">
                {addresses.map((address) => (
                  <AddressCard
                    key={address.id}
                    address={address}
                    isSelected={selectedAddress?._id === address._id}
                    onSelect={() => setSelectedAddress(address)}
                    onEdit={() => {
                      setEditAddressData(address);
                      setShowAddressModal(true);
                    }}
                    onDelete={() => setDeleteAddress(address)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center text-sm text-gray-500 mt-10">
                No addresses found. Please add one to continue.
              </div>
            )}
          </div>

          {/* ================= STICKY FOOTER CTA ================= */}
          {addresses.length > 0 && (
            <footer className="fixed bottom-0 left-0 w-full md:static md:w-auto bg-white border-t p-4 flex flex-col gap-3 md:rounded-b-lg">

              {/* SELECT ADDRESS CTA */}
              {selectedAddress && (
                <button
                  onClick={() => {
                    onSelectAddress(selectedAddress);
                    closeModal();
                  }}
                  className="w-full py-3 rounded-lg bg-BluntRed text-white text-sm font-medium"
                >
                  Proceed with this Puja Address
                </button>
              )}

              {/* ADD NEW ADDRESS CTA */}
              <button
                onClick={() => {
                  setEditAddressData(null);
                  setShowAddressModal(true);
                }}
                className="w-full py-3 rounded-lg border border-BluntRed text-BluntRed text-sm font-medium"
              >
                + Add New Address
              </button>
            </footer>
          )}
        </div>
      </div>

      {/* ================= ADD / EDIT FLOW MODAL ================= */}
      {showAddressModal && (
        <AddressFlowModal
          userData={userData}
          editAddressData={editAddressData}
          otpHandlers={otpHandlers}
          handleAddAddress={async (data) => {
            await handleAddAddress(data);
            await fetchAddresses();
            closeFlowModal();
          }}
          closeAddressModal={closeFlowModal}
        />
      )}

      {/* ================= DELETE CONFIRMATION MODAL ================= */}
      {deleteAddress && (
        <ConfirmDeleteModal
          title="Delete Address?"
          description="This address will be permanently removed."
          onCancel={() => setDeleteAddress(null)}
          onConfirm={async () => {
            await handleDeleteAddress(deleteAddress._id);
            await fetchAddresses();
            setDeleteAddress(null);
          }}
        />
      )}
    </>
  );
};

export default AddressListing;
